import { StrictLocalizeFunction } from "@faire/web--source/common/localization";

export const BUSINESS_NAME_MIN_CHARS = 3;
export const BUSINESS_IDENTIFIER_MAX_CHARS = 100;
export const CERTIFICATE_NUMBER_MIN_CHARS = 5;
export const CERTIFICATE_NUMBER_MAX_CHARS = 100;
export const FREE_FORM_QUESTION_MIN_CHARS = 10;
export const RADIO_TEXT_INPUT_MAX_CHARS = 100;
export const STORE_DESCRIPTION_MIN_CHARS = 10;
export const TEXT_INPUT_MAX_CHARS = 250;
export const TEXT_AREA_ROWS = 5;

export enum Errors {
  // generic errors - used by multiple fields
  NONE = 0,
  IS_EMPTY,
  RADIO_TEXT_INPUT_OVER_MAX_CHARS,
  TEXT_INPUT_OVER_MAX_CHARS,

  // specific errors - used by a single field
  BUSINESS_NAME_UNDER_MIN_CHARS,
  BUSINESS_IDENTIFIER_OVER_MAX_CHARS,
  CERTIFICATE_NUMBER_UNDER_MIN_CHARS,
  CERTIFICATE_NUMBER_OVER_MAX_CHARS,
  EMAIL_INVALID,
  INVALID_URL,
  FREE_FORM_QUESTION_UNDER_MIN_CHARS,
  STORE_DESCRIPTION_UNDER_MIN_CHARS,
  RADIO_WITH_EMPTY_INPUT_SELECTED,
}

export const errorTypeToErrorMsg = (
  strictLocalize: StrictLocalizeFunction
): Map<Errors, string | undefined> =>
  new Map([
    [Errors.NONE, undefined],
    [
      Errors.IS_EMPTY,
      strictLocalize({
        defaultMessage: "Required",
        description: { text: "Indicate the field in a form is required" },
      }),
    ],
    [
      Errors.BUSINESS_NAME_UNDER_MIN_CHARS,
      getUnderMinCharsErrorMsg(BUSINESS_NAME_MIN_CHARS, strictLocalize),
    ],
    [
      Errors.BUSINESS_IDENTIFIER_OVER_MAX_CHARS,
      getOverMaxCharsErrorMsg(BUSINESS_IDENTIFIER_MAX_CHARS, strictLocalize),
    ],
    [
      Errors.STORE_DESCRIPTION_UNDER_MIN_CHARS,
      getUnderMinCharsErrorMsg(STORE_DESCRIPTION_MIN_CHARS, strictLocalize),
    ],
    [
      Errors.EMAIL_INVALID,
      strictLocalize({
        defaultMessage: "Invalid email",
        description: {
          text: "The email address that was entered is incorrect",
        },
      }),
    ],
    [
      Errors.INVALID_URL,
      strictLocalize({
        defaultMessage: "Please enter a valid URL",
        description: {
          text: "The URL that was entered is invalid.",
        },
      }),
    ],
    [
      Errors.FREE_FORM_QUESTION_UNDER_MIN_CHARS,
      getUnderMinCharsErrorMsg(FREE_FORM_QUESTION_MIN_CHARS, strictLocalize),
    ],
    [
      Errors.TEXT_INPUT_OVER_MAX_CHARS,
      getOverMaxCharsErrorMsg(TEXT_INPUT_MAX_CHARS, strictLocalize),
    ],
    [
      Errors.RADIO_TEXT_INPUT_OVER_MAX_CHARS,
      getOverMaxCharsErrorMsg(RADIO_TEXT_INPUT_MAX_CHARS, strictLocalize),
    ],
    [
      Errors.CERTIFICATE_NUMBER_UNDER_MIN_CHARS,
      getUnderMinCharsErrorMsg(CERTIFICATE_NUMBER_MIN_CHARS, strictLocalize),
    ],
    [
      Errors.CERTIFICATE_NUMBER_OVER_MAX_CHARS,
      getOverMaxCharsErrorMsg(CERTIFICATE_NUMBER_MAX_CHARS, strictLocalize),
    ],
  ]);

const getUnderMinCharsErrorMsg = (
  minChars: number,
  strictLocalize: StrictLocalizeFunction
): string =>
  strictLocalize(
    {
      defaultMessage: "Include at least {minCharsNumber} characters",
      description: {
        text: "Indicate the field in a form requires some characters to be valid",
      },
    },
    { minCharsNumber: minChars }
  );

const getOverMaxCharsErrorMsg = (
  maxChars: number,
  strictLocalize: StrictLocalizeFunction
): string =>
  strictLocalize(
    {
      defaultMessage: "Cannot be longer than {maxCharsNumber} characters",
      description: {
        text: "Indicate the field in a form cannot exceed a maximum number of characters",
      },
    },
    { maxCharsNumber: maxChars }
  );
